import { RefObject, useEffect } from 'react';

/*
  Spread onto a bar that parks itself at the top of the viewport — the app bar, the mobile header,
  a sticky hero. A table header has to come to rest below all of them, and a screen can stack more
  than one, so they are tagged and measured rather than assumed: their heights vary with search
  bars, tabs and breakpoints.
*/
const STICKY_TOP_BAR_ATTR = 'data-sticky-top-bar';
export const stickyTopBarProps = { [STICKY_TOP_BAR_ATTR]: true };

const topBars = () => Array.from(document.querySelectorAll<HTMLElement>(`[${STICKY_TOP_BAR_ATTR}]`));

// Where the lowest of them comes to rest: its own parked offset plus its height. A bar hidden at
// this breakpoint measures zero, so only the ones actually on screen count.
const topBarInset = (bars: HTMLElement[]) =>
  bars.reduce((inset, bar) => {
    const parkedAt = parseFloat(getComputedStyle(bar).top);
    return Math.max(inset, (Number.isFinite(parkedAt) ? parkedAt : 0) + bar.offsetHeight);
  }, 0);

export const stickyTopBarsInset = () => topBarInset(topBars());

const HEADER_KEYFRAMES = 'stickyTableHeader';
const BAR_KEYFRAMES = 'squareOnStickyHeader';

const STICKY_HEADER_START_VAR = '--sticky-header-start';
const STICKY_HEADER_END_VAR = '--sticky-header-end';
const STICKY_HEADER_TRAVEL_VAR = '--sticky-header-travel';

// Above the sticky first column (1) and the group shadow overlay (2) that scroll under it.
export const stickyHeaderZIndex = (index: number) => (index === 0 ? 4 : 3);

// The same test the header's @supports rules make, so the hook only runs where the styles use it.
const supportsScrollTimeline = () => typeof CSS !== 'undefined' && CSS.supports('animation-timeline', 'scroll()');

/*
  A scroll-timeline animation resolves its range when it starts and keeps it. Re-measuring updates
  the custom properties the range is written in, and the computed value changes with them, but the
  running animation goes on using the offsets it began with — so a header measured before the page
  settled stays aimed at where the table used to be. Restarting it is what makes it read the range
  again.
*/
const reaim = (root: Element, keyframes: string) => {
  root.getAnimations({ subtree: true }).forEach((animation) => {
    if ((animation as CSSAnimation).animationName !== keyframes) return;
    animation.cancel();
    animation.play();
  });
};

/*
  The table container scrolls horizontally, which makes it the sticky scrollport for anything inside
  it — and it never scrolls vertically, so `position: sticky` can never pin the header to the page.
  The page scroll moves it down over its own rows instead, as a scroll-timeline animation the
  compositor drives, held against the top of the viewport until the table ends. This only measures
  the scroll offsets that animation runs between; the header travels exactly as far as its range is
  long, so it moves at the same speed as the page and holds still against the viewport edge.
  Browsers without scroll timelines get no range and no animation, and their header simply scrolls
  away with the rest of the page.
*/
const useStickyTableEdges = (containerRef: RefObject<HTMLDivElement | null>, enabled: boolean) => {
  useEffect(() => {
    const container = containerRef.current;
    if (!enabled || !container || !supportsScrollTimeline()) return;

    const measure = () => {
      const table = container.querySelector('table');
      const header = container.querySelector('thead');
      if (!table || !header) return;

      // Re-read them: a screen can swap its bars out (a hero turns sticky, a breakpoint changes)
      // without the table ever remounting.
      const bars = topBars();
      const containerTop = container.getBoundingClientRect().top + window.scrollY;
      // Measured off the table, not the container: the progress bar that shows while the screen
      // revalidates renders above it, and the header parks from wherever the table actually starts.
      // The table itself is never transformed, unlike the header cells this drives.
      const tableTop = table.getBoundingClientRect().top + window.scrollY;
      const headerStart = Math.max(tableTop - topBarInset(bars), 0);
      // Stop at the last row so the header never floats out of its own table. clientHeight leaves
      // out the container's horizontal scrollbar, which the rows don't reach into either.
      const headerTravel = Math.max(containerTop + container.clientHeight - tableTop - header.offsetHeight, 0);

      container.style.setProperty(STICKY_HEADER_TRAVEL_VAR, `${headerTravel}px`);
      container.style.setProperty(STICKY_HEADER_START_VAR, `${headerStart}px`);
      container.style.setProperty(STICKY_HEADER_END_VAR, `${headerStart + headerTravel}px`);
      // A bar squares its rounded corners off from here, where the header parks against it.
      bars.forEach((bar) => bar.style.setProperty(STICKY_HEADER_START_VAR, `${headerStart}px`));

      reaim(container, HEADER_KEYFRAMES);
      bars.forEach((bar) => reaim(bar, BAR_KEYFRAMES));
    };

    measure();
    window.addEventListener('resize', measure);
    // Expanding a group or loading a page moves the table and changes how far the header travels.
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    // A bar grows and shrinks with its search input and tabs, which moves where the header parks.
    const observedBars = topBars();
    observedBars.forEach((bar) => observer.observe(bar));
    // Anything above the table growing moves the table without resizing it, which the observers
    // above would miss; the page's own height changes with it.
    observer.observe(document.body);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      [STICKY_HEADER_TRAVEL_VAR, STICKY_HEADER_START_VAR, STICKY_HEADER_END_VAR].forEach((property) =>
        container.style.removeProperty(property),
      );
      observedBars.forEach((bar) => bar.style.removeProperty(STICKY_HEADER_START_VAR));
    };
  }, [containerRef, enabled]);
};

/*
  Moves the header down over its own rows as the page scrolls past it, across the range the hook
  measures, as a scroll-timeline animation the compositor drives. Everything lives behind @supports:
  browsers without scroll timelines get no sticky header at all, and a time-based animation there
  would just jump straight to its end. It is inert on tables the hook doesn't run on too, where the
  range is never published.
*/
export const stickyHeaderOffsetStyle = {
  '@supports (animation-timeline: scroll())': {
    willChange: 'transform',
    animationName: HEADER_KEYFRAMES,
    animationTimeline: 'scroll(root block)',
    animationRange: `var(${STICKY_HEADER_START_VAR}, 0px) var(${STICKY_HEADER_END_VAR}, 0px)`,
    animationTimingFunction: 'linear',
    animationFillMode: 'both',
    [`@keyframes ${HEADER_KEYFRAMES}`]: {
      to: { transform: `translateY(var(${STICKY_HEADER_TRAVEL_VAR}, 0px))` },
    },
  },
};

/*
  Header cells of a table that opts in through the schema's stickyHeader, whatever its type. Spread
  as its own sx entry rather than merged into getTableColumnStyle, so a type keeps the positioning it
  already sets on its own header cells and only the movement rides on the schema's say-so.
*/
export const stickyHeaderCellStyle = (index: number, background: string) => ({
  '&.MuiTableCell-root': {
    position: 'sticky',
    top: 0,
    zIndex: stickyHeaderZIndex(index),
    // The row's background sits behind the cells; they need their own once they lift off it.
    background,
    ...stickyHeaderOffsetStyle,
  },
});

/*
  For the fixed bar a header parks under: rounded bottom corners leave notches above the parked
  header, so they square off over the 1px of scroll where it starts sticking. The default offset sits
  beyond any page, leaving the corners alone on screens with no sticky table. Browsers without scroll
  timelines keep them rounded throughout, which is the look before any of this.
*/
export const squareOnStickyHeaderStyle = {
  '@supports (animation-timeline: scroll())': {
    animationName: BAR_KEYFRAMES,
    animationTimeline: 'scroll(root block)',
    animationRange: `var(${STICKY_HEADER_START_VAR}, 999999px) calc(var(${STICKY_HEADER_START_VAR}, 999999px) + 1px)`,
    animationTimingFunction: 'linear',
    animationFillMode: 'both',
    [`@keyframes ${BAR_KEYFRAMES}`]: {
      to: { borderRadius: 0 },
    },
  },
};

export default useStickyTableEdges;
